import '../../styles/tutorials.css';
import { SiteLink as Link } from '../../components/SiteLink';
import { PageShell } from '../../components';
import { Breadcrumbs } from '../../components/tutorials/Breadcrumbs';
import { TutorialSidebar } from '../../components/tutorials/Sidebar';
import { TUTORIALS, testedParts, topicLabel, tutorialPath, tutorialsInTopic } from '../../content';
import type { Tutorial, TutorialSection } from '../../content';

/**
 * Languages a build guide can exist for, in the order they are offered. A
 * language is shown only when build-mcp-server-<id> is really there, so the
 * grid grows as the guides land instead of linking at missing pages.
 */
const LANGUAGES = [
  ['python', 'Python'],
  ['typescript', 'TypeScript'],
  ['go', 'Go'],
  ['rust', 'Rust'],
  ['csharp', 'C#'],
  ['java', 'Java'],
  ['kotlin', 'Kotlin'],
  ['swift', 'Swift'],
  ['ruby', 'Ruby'],
  ['php', 'PHP'],
] as const;

function LanguageGrid() {
  const built = LANGUAGES.filter(([id]) => TUTORIALS.some((t) => t.slug === `build-mcp-server-${id}`));
  if (built.length === 0) return null;
  return (
    <nav className="tu-langs" aria-label="Build a server in">
      {built.map(([id, label]) => (
        <Link key={id} className="tu-lang" to={tutorialPath(`build-mcp-server-${id}`)}>
          {label}
        </Link>
      ))}
    </nav>
  );
}

function TutorialRow({ tutorial }: { tutorial: Tutorial }) {
  return (
    <li>
      <Link to={tutorialPath(tutorial.slug)}>
        <span className="tu-item-title">{tutorial.title}</span>
        <span className="tu-item-desc">{tutorial.description}</span>
        <span className="tu-item-meta">
          {tutorial.readMinutes} min · {testedParts(tutorial.tested).label} {testedParts(tutorial.tested).value}
        </span>
      </Link>
    </li>
  );
}

/**
 * A section hub at /tutorials/<hub>: what the section covers, then every
 * tutorial in it under its topic heading. One of these is the page a reader
 * lands on from search for the whole subject rather than one task.
 */
export default function SectionHub({ section }: { section: TutorialSection }) {
  return (
    <PageShell>
      <div className="s-wrap tu">
        <TutorialSidebar section={section} />
        <div className="tu-main">
          <Breadcrumbs section={section} />
          <header className="tu-head">
            <h1>{section.label}</h1>
            <p className="tu-lede">{section.intro}</p>
          </header>
          {section.languageGrid && <LanguageGrid />}
          {section.topics.map((topic) => {
            const items = tutorialsInTopic(topic);
            if (items.length === 0) return null;
            return (
              <section key={topic} className="tu-group" aria-labelledby={`tu-${topic}`}>
                {/* In a one-topic section the topic heading would repeat the h1 word for word. */}
                <h2 id={`tu-${topic}`} className={section.topics.length === 1 ? 'sr-only' : undefined}>
                  {topicLabel(topic)}
                </h2>
                <ul>
                  {items.map((t) => (
                    <TutorialRow key={t.slug} tutorial={t} />
                  ))}
                </ul>
              </section>
            );
          })}
        </div>
      </div>
    </PageShell>
  );
}
